"use client";

import { Search, ClipboardCheck, FileText, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { motion } from "framer-motion";
const steps = [{
  number: "01",
  title: "SOPRALLUOGO",
  Icon: Search,
  description: "Analisi tecnica preliminare degli impianti aeraulici e dei sistemi di estrazione per mappare lo stato di conservazione e le criticità funzionali."
}, {
  number: "02",
  title: "ISPEZIONE",
  Icon: ClipboardCheck,
  description: "Ispezione visiva e video endoscopica approfondita per valutare l'accumulo di depositi e l'integrità strutturale dei condotti e dei componenti."
}, {
  number: "03",
  title: "INTERVENTO",
  Icon: ShieldCheck,
  description: "Esecuzione della bonifica tecnica specialistica mediante tecnologie avanzate, garantendo la riqualificazione igienico-funzionale dell'intero impianto."
}, {
  number: "04",
  title: "VERIFICA",
  Icon: FileText,
  description: "Verifica finale con report fotografico dettagliato prima/dopo, attestante l'efficienza ripristinata e la conformità normativa dell'impianto."
}];
export default function MetodoOperativoAera() {
  return <section data-section-id="3883" id="metodooperativoaera" className="bg-background text-foreground relative overflow-hidden py-32 md:py-[200px]">
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-default text-[10px] font-medium tracking-[0.3em] uppercase text-foreground/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; IL METODO
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: "easeOut"
      }} className="grid grid-cols-1 overflow-hidden md:grid-cols-12">
          <div className="bg-primary p-8 md:col-span-7 md:p-16 lg:p-20">
            <div className="mb-8">
              <span className="font-mono text-xs tracking-widest text-accent uppercase">
                [REF_TECH_2024]
              </span>
            </div>
            <h1 className="font-sans font-semibold leading-[1.1] tracking-tighter text-white" style={{
            fontSize: "clamp(2.5rem, 5vw, 4.5rem)"
          }}>
              Cappe professionali e tubazioni di estrazione cucina
            </h1>
            <p className="mt-8 max-w-xl font-sans text-lg leading-relaxed text-white/80 md:text-xl">
              Interventi specialistici su cappe inox, filtri, plenum, tubi di estrazione, condotti interni, curve, tratti verticali, ventilatori e componenti soggetti ad accumuli di grassi, oli e vapori condensati.
            </p>
          </div>
          <div className="relative min-h-[400px] md:col-span-5">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/industrial_extraction_system.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/industrial_extraction_system.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/industrial_extraction_system.webp-wvc-sizes" alt="Industrial kitchen extraction system schematic" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-primary/5" />
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
            backgroundSize: '24px 24px'
          }} />
          </div>
        </motion.div>

        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-3 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-1.5 bg-primary" />
                        <span className="h-px w-1 bg-primary/60" />
                        <span className="h-px w-0.5 bg-primary/30" />
                      </div>
                    </div>}

                  <div className="relative h-[380px] overflow-hidden border border-border bg-card backdrop-blur-xl shadow-lg transition-all duration-500 group-hover:-translate-y-1 group-hover:border-primary group-hover:shadow-xl">
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-default text-[120px] font-extrabold leading-none tracking-tighter text-primary/10 transition-colors duration-[400ms] group-hover:text-primary" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-foreground/80 transition-colors duration-300 group-hover:text-primary" strokeWidth={1} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-foreground/30 transition-all duration-500 group-hover:w-16 group-hover:bg-primary" />
                        <h3 className="font-default text-2xl font-bold tracking-[0.15em] uppercase text-secondary">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-foreground/65">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 sm:flex-row sm:items-center">
            <p className="font-default text-xs tracking-[0.25em] uppercase text-foreground/50">
              Quattro fasi &mdash; un unico obiettivo
            </p>
            <p className="font-default italic text-sm text-foreground/60">
              Riqualificazione tecnica certificata.
            </p>
          </div>
        </div>
      </div>
    </section>;
}